'use client'

import { Image, Carousel, Dialog, Icon, Button } from '@vinted/web-ui'
import { ReactNode, useCallback, useEffect, useState } from 'react'
import { X24 } from '@vinted/monochrome-icons'
import classNames from 'classnames'
import { InView } from 'react-intersection-observer'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { userViewItemGallery } from '@marketplace-web/domain/item-page-data'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { clickEvent } from '_libs/common/event-tracker/events'

import { ItemPageGalleryPluginModel } from '../types'
import Thumbnails from './Thumbnails'
import FavouriteButton from '../FavouriteButton'

type Props = {
  data: ItemPageGalleryPluginModel
  favouriteButton?: ReactNode
}

const DesktopImageCarousel = ({ data, favouriteButton }: Props) => {
  const [isCarouselOpen, setIsCarouselOpen] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [mainIndex, setMainIndex] = useState(0)
  const [dialogIndex, setDialogIndex] = useState(0)
  const { track } = useTracking()
  const translate = useTranslate('item.gallery.a11y')

  const handleImageClick = useCallback(() => {
    setDialogIndex(mainIndex)
    setIsCarouselOpen(true)
    setIsFullscreen(false)
  }, [mainIndex])

  const handleDialogClose = useCallback(() => {
    setIsCarouselOpen(false)
  }, [])

  const handleThumbnailClick = useCallback((index: number) => {
    setMainIndex(index)
  }, [])

  useEffect(() => {
    if (!isCarouselOpen) {
      setMainIndex(dialogIndex)
    }
  }, [isCarouselOpen, dialogIndex])

  const handleImageView =
    (index: number, imageView: 'item_page' | 'fullscreen') => (inView: boolean) => {
      if (!inView) return

      track(
        userViewItemGallery({
          itemId: data.id,
          imagePosition: index + 1,
          isLoop: true,
          imageCount: data.photos.length,
          contentType: 'gallery_image',
          galleryContainsAd: false,
          imageView,
        }),
      )
    }

  const handleFullscreenClick = () => {
    setIsFullscreen(!isFullscreen)

    track(
      clickEvent({
        target: ClickableElement.ImageModal,
        targetDetails: JSON.stringify({
          position: dialogIndex + 1,
          item_id: data.id.toString(),
          action: isFullscreen ? 'zoom_out' : 'zoom_in',
        }),
      }),
    )
  }

  return (
    <>
      <div className="desktop-image-gallery-plugin__thumbnails-container">
        <Thumbnails
          photos={data.photos}
          selectedPhotoId={mainIndex}
          setSelectedPhotoId={handleThumbnailClick}
          itemId={data.id}
          target={ClickableElement.ImageThumbnail}
          imageView="item_page"
        />
      </div>
      <div className="desktop-image-carousel">
        <div className="desktop-image-carousel__favourite-button">
          {favouriteButton || (
            <FavouriteButton
              isFavourited={data.isFavourite}
              itemId={data.id}
              initialFavouriteCount={data.favouriteCount}
              itemUserId={data.ownerId}
            />
          )}
        </div>
        <Carousel
          arrows={data.photos.length > 1 ? 'outside' : 'hidden'}
          hideNavigation
          isInfinite
          navigationWithKeyboard
          slides={data.photos.map((photo, index) => (
            <button
              key={photo.id}
              type="button"
              onClick={handleImageClick}
              data-testid={`item-photo-${photo.id}-button`}
              aria-label={translate('current_order_position', {
                position: index + 1,
                total: data.photos.length,
              })}
            >
              <InView
                as="div"
                className="desktop-image-carousel__image"
                onChange={handleImageView(index, 'item_page')}
                triggerOnce
              >
                <Image
                  src={photo.url}
                  alt={data.photos.length === 1 ? `${data.title}` : `${data.title} ${index + 1}`}
                  key={photo.id}
                  scaling="cover"
                  styling="rounded"
                />
              </InView>
            </button>
          ))}
          styling="floating"
          onSlideInteract={setMainIndex}
          index={mainIndex}
        />
        <Dialog
          show={isCarouselOpen}
          defaultCallback={handleDialogClose}
          testId="photo-gallery-modal-desktop"
          contentDimensions={{
            width: '100vw',
            height: '100vh',
          }}
          className="desktop-photo-gallery-modal"
          isModal={false}
        >
          <div className="desktop-photo-gallery-modal__content">
            <div className="desktop-photo-gallery-modal__close-button">
              <Button
                icon={<Icon name={X24} title="Close" color={Icon.Color.GreyscaleLevel1} />}
                inline
                styling={Button.Styling.Filled}
                onClick={handleDialogClose}
                inverse
                aria-label={translate('modal.actions.close')}
              />
            </div>
            <div className="desktop-photo-gallery-modal__thumbnails-container">
              <div className="desktop-photo-gallery-modal__thumbnails">
                <Thumbnails
                  photos={data.photos}
                  selectedPhotoId={dialogIndex}
                  setSelectedPhotoId={setDialogIndex}
                  itemId={data.id}
                  target={ClickableElement.ImageThumbnailModal}
                  imageView="fullscreen"
                />
              </div>
            </div>
            <div
              className={classNames('desktop-photo-gallery-modal__image-container', {
                'u-overflow-hidden u-flexbox': !isFullscreen,
              })}
            >
              <button
                type="button"
                onClick={handleFullscreenClick}
                className={classNames('desktop-photo-gallery-modal__image-button', {
                  'desktop-photo-gallery-modal__image-button--fullscreen': isFullscreen,
                })}
                data-testid="desktop-photo-gallery-modal__image-button"
                aria-label={translate('current_order_position', {
                  position: dialogIndex + 1,
                  total: data.photos.length,
                })}
              >
                <InView
                  as="div"
                  onChange={handleImageView(dialogIndex, 'fullscreen')}
                  className={classNames('desktop-photo-gallery-modal__image', {
                    'desktop-photo-gallery-modal__image--fullscreen': isFullscreen,
                    'desktop-photo-gallery-modal__image--not-fullscreen': !isFullscreen,
                  })}
                  triggerOnce
                >
                  <Image
                    src={data.photos[dialogIndex]?.url}
                    alt={
                      data.photos.length === 1
                        ? `${data.title}`
                        : `${data.title} ${dialogIndex + 1}`
                    }
                    scaling={isFullscreen ? 'cover' : 'contain'}
                  />
                </InView>
              </button>
            </div>
          </div>
        </Dialog>
      </div>
    </>
  )
}

export default DesktopImageCarousel
